import { Appointment, AppointmentPolicy } from "../../types/appointments/AppointmentTypes";

/**
 * Patient-side cancellation gating (S6, ClickUp 86d3n989m).
 *
 * The backend is authoritative: healthcare re-checks `allow_cancellation` and
 * `cancellation_notice_hours` on every cancel/reschedule and answers 403/422. This module only
 * decides what the patient SEES, so the refusal is not a surprise at click time.
 */

/**
 * Is this appointment already cancelled?
 *
 * `status` is a free-text column with no enum. The patient path writes lowercase `cancelled`,
 * while the practitioner status list (`consultStatuses`) writes `Cancelled` and `Cancelled within
 * 24h`. The old `status === "cancelled"` comparison matched none of the practitioner values, so a
 * practitioner-cancelled appointment still showed the patient a live Cancel button.
 */
export const isCancelledStatus = (status?: string | null): boolean => !!status && status.toLowerCase().includes("cancelled");

export type CancelAvailability =
	| { kind: "hidden" }
	| { kind: "disabled"; reason: string }
	| { kind: "enabled" };

/**
 * Whether the patient may cancel this appointment — and if not, whether to say so.
 *
 * - past / already cancelled → hidden (nothing to cancel).
 * - `allow_cancellation = false` → hidden: the clinic never offers online cancellation, so an
 *   affordance would be noise.
 * - inside `cancellation_notice_hours` → DISABLED with the reason, not hidden: the patient has a
 *   real appointment they may well want to cancel, and needs to know why they cannot and what to
 *   do instead.
 * - policy absent (an older/failed read) → enabled; the backend still refuses if policy says so.
 */
export const getCancelAvailability = (appointment: Appointment, policy?: AppointmentPolicy | null, now: Date = new Date()): CancelAvailability => {
	const startTime = appointment.start_time ? new Date(appointment.start_time) : null;

	if (startTime && startTime.getTime() < now.getTime()) return { kind: "hidden" };
	if (isCancelledStatus(appointment.status)) return { kind: "hidden" };

	if (!policy) return { kind: "enabled" };
	if (!policy.allow_cancellation) return { kind: "hidden" };

	const noticeHours = policy.cancellation_notice_hours ?? 0;

	if (startTime && noticeHours > 0) {
		const deadline = startTime.getTime() - noticeHours * 60 * 60 * 1000;

		if (now.getTime() > deadline) {
			return {
				kind: "disabled",
				reason: `Cancellations require ${noticeHours} hours' notice — please call the clinic`,
			};
		}
	}

	return { kind: "enabled" };
};
